import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@mondivago/supabase';
import { buildSupplierPath } from '@/shared/seo/seoRouting';
import { getSupabaseImageUrl } from '@/shared/lib/image';
import { useSupplierProfile, useChat, useSupplierMordModal } from '@/hooks/suppliers';
import { UnifiedSidebar, UnifiedSidebarMenuItem } from '@/components/sidebars/UnifiedSidebar';
import { Tooltip } from '@/components/ui';
import { SupplierMordModal, SupportTicketModal } from '@/components/modals';
import { NotificationDropdown } from '@/components/supplier/NotificationDropdown';
import {
  BarChart2,
  Building2,
  Calendar,
  Home,
  Inbox,
  Settings,
  Wallet,
  Activity,
  BookMarked,
  Users,
  MessageSquare,
  Headset,
  FileText,
  Tag,
  Scale,
  type LucideIcon,
} from 'lucide-react';

interface SupplierSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  roles: string[];
}

const MENU_ITEMS: MenuItem[] = [
  {
    path: '/supplier/dashboard',
    label: 'Dashboard', // supplier company roles use 'agency'
    icon: Home,
    roles: ['individual', 'agency'],
  },
  {
    path: '/supplier/calendar',
    label: 'Calendar',
    icon: Calendar,
    roles: ['individual', 'agency'],
  },
  { path: '/supplier/bookings', label: 'Bookings', icon: BookMarked, roles: ['individual', 'agency'] },
  {
    path: '/supplier/messages',
    label: 'Inbox',
    icon: Inbox,
    roles: ['individual', 'agency'],
  },
  {
    path: '/supplier/chat',
    label: 'Chat',
    icon: MessageSquare,
    roles: ['individual', 'agency'],
  },
  { path: '/supplier/activities', label: 'Activities', icon: Activity, roles: ['individual', 'agency'] },
  { path: '/supplier/analytics', label: 'Analytics', icon: BarChart2, roles: ['individual', 'agency'] },
  {
    path: '/supplier/reports',
    label: 'Reports',
    icon: FileText,
    roles: ['individual', 'agency'],
  },
  { path: '/supplier/wallet', label: 'Balance', icon: Wallet, roles: ['individual', 'agency'] },
  { path: '/supplier/promotions', label: 'Promotions', icon: Tag, roles: ['individual', 'agency'] },
  { path: '/supplier/team', label: 'Team', icon: Users, roles: ['agency'] },
  {
    path: '/supplier/supplier-settings',
    label: 'Supplier Settings',
    icon: Building2,
    roles: ['individual', 'agency'],
  },
  {
    path: '/supplier/account-settings',
    label: 'Account Settings',
    icon: Settings,
    roles: ['individual', 'agency'],
  },
];

export function SupplierSidebar({ isCollapsed, setIsCollapsed }: SupplierSidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, role } = useAuth();
  const { agency, profile, workerRole } = useSupplierProfile();
  const { unreadCount } = useChat();
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const {
    showModal: showLegalModal,
    activeAgreement,
    hasNewVersion,
    loading: legalLoading,
    handleClose: closeLegalModal,
    openModal: openLegalModal,
    refresh: refreshLegalModal,
    acceptAgreement,
  } = useSupplierMordModal();

  const isTeamWorker = !!agency || (!!workerRole && workerRole.toLowerCase() !== 'owner') || profile === null;

  const hiddenForWorkers = useMemo(
    () => [
      '/supplier/reports',
      '/supplier/team',
      '/supplier/wallet',
      '/supplier/promotions',
      '/supplier/supplier-settings',
      '/supplier/analytics',
    ],
    []
  );

  useEffect(() => {
    if (isTeamWorker && hiddenForWorkers.some((path) => location.pathname.startsWith(path))) {
      void navigate('/supplier/dashboard', { replace: true });
    }
  }, [isTeamWorker, hiddenForWorkers, location.pathname, navigate]);

  const userPhoto =
    profile?.photo_url ??
    agency?.photo_url ??
    (user?.user_metadata?.avatar_url as string | undefined) ??
    (user?.user_metadata?.picture as string | undefined) ??
    null;

  const profileInitials = useMemo(() => {
    const name =
      profile?.company_name ??
      agency?.company_name ??
      (user?.user_metadata?.full_name as string | undefined) ??
      (user?.user_metadata?.name as string | undefined) ??
      '';
    const parts = name.trim().split(' ');
    if (parts.length < 2) {
      return name ? name.slice(0, 2).toUpperCase() : 'MG';
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [profile?.company_name, agency?.company_name, user?.user_metadata]);

  const [dynamicLabel, setDynamicLabel] = useState<string | null>(null);
  const [prevPathname, setPrevPathname] = useState(location.pathname);

  useEffect(() => {
    const handleSetLabel = (e: CustomEvent<string>) => {
      setDynamicLabel(e.detail);
    };
    window.addEventListener('set-breadcrumb-label', handleSetLabel as EventListener);
    return () => {
      window.removeEventListener('set-breadcrumb-label', handleSetLabel as EventListener);
    };
  }, []);

  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);
    if (dynamicLabel !== null) setDynamicLabel(null);
  }

  const breadcrumbs = useMemo(() => {
    const allItems = [...MENU_ITEMS];
    const DASHBOARD_PATH = '/supplier/dashboard';
    const segments = [{ label: 'Supplier', to: DASHBOARD_PATH }];

    let bestMatch: { label: string; to: string } | null = null;
    for (const item of allItems) {
      if (item.path === DASHBOARD_PATH) continue;
      if (location.pathname.startsWith(item.path)) bestMatch = { label: item.label, to: item.path };
    }

    if (bestMatch && location.pathname !== DASHBOARD_PATH) {
      segments.push(bestMatch);
      if (location.pathname !== bestMatch.to && location.pathname.startsWith(`${bestMatch.to}/`)) {
        const sub = location.pathname.replace(`${bestMatch.to}/`, '');
        const subParts = sub.split('/');

        if (dynamicLabel) {
          segments.push({ label: dynamicLabel, to: location.pathname });
        } else if (bestMatch.to === '/supplier/activities' && subParts.length >= 2 && subParts[1] === 'edit') {
          segments.push({ label: 'Edit', to: location.pathname });
        } else {
          const subLabel = subParts[0]
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          segments.push({ label: subLabel, to: location.pathname });
        }
      }
    }
    return segments;
  }, [location.pathname, dynamicLabel]);

  const handleSignOut = async () => {
    await signOut();
    void navigate('/', { replace: true });
  };

  const filteredMenuItems: UnifiedSidebarMenuItem[] = MENU_ITEMS.filter((item) => {
    if (isTeamWorker && hiddenForWorkers.includes(item.path)) {
      return false;
    }
    return !role || item.roles.includes(role as string);
  }).map((item) => ({
    path: item.path,
    label: item.label,
    icon: item.icon,
    end: item.end ?? item.path === '/supplier/dashboard',
    badge: item.path === '/supplier/chat' ? unreadCount : undefined,
  }));

  const headerActions = (
    <div className="flex items-center gap-3">
      <NotificationDropdown buttonClassName="relative group flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200 text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:scale-95" />
      <Link
        to={
          agency?.short_id
            ? buildSupplierPath(agency.company_name, agency.short_id)
            : profile?.id
              ? buildSupplierPath(profile.company_name ?? 'Supplier', profile.short_id ?? undefined)
              : '#'
        }
        className="flex items-center transition-transform hover:scale-105 active:scale-95"
        title={agency?.id ? 'Parent Profile' : 'My Profile'}
      >
        <div className="relative overflow-visible md:h-9 md:w-9 h-10 w-10">
          <div className="h-full w-full overflow-hidden rounded-full border border-gray-100 md:border-gray-100 ring-2 ring-lime-400 md:ring-0 ring-offset-2 ring-offset-white bg-gray-50 shadow-sm">
            {userPhoto ? (
              <img
                src={getSupabaseImageUrl(userPhoto, 'public', { width: 96, height: 96, format: 'webp' })}
                alt="Avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-lime-100 text-xs font-black text-lime-900">
                {profileInitials}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );

  // Contact email used for support tickets — read-only in the modal.
  const contactEmail = profile?.contact_email ?? agency?.contact_email ?? user?.email ?? '';

  const legalButton = (
    <Tooltip content="Legal Agreement" position="right" className="w-full" disabled={!isCollapsed}>
      <button
        type="button"
        onClick={openLegalModal}
        className={`group relative flex items-center rounded-xl py-3 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'w-full gap-3 px-3'}`}
      >
        <Scale className="h-5 w-5 shrink-0" />
        {!isCollapsed && <span>Legal Agreement</span>}
      </button>
    </Tooltip>
  );

  const supportButton = (
    <Tooltip content="Support" position="right" className="w-full" disabled={!isCollapsed}>
      <button
        type="button"
        onClick={() => setSupportModalOpen(true)}
        className={`group relative flex items-center rounded-xl py-3 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'w-full gap-3 px-3'}`}
      >
        <Headset className="h-5 w-5 shrink-0" />
        {!isCollapsed && <span>Support</span>}
      </button>
    </Tooltip>
  );

  return (
    <>
      <UnifiedSidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        brandLink="/"
        menuItems={filteredMenuItems}
        breadcrumbs={breadcrumbs}
        headerActions={headerActions}
        onSignOut={handleSignOut}
        showHelp={false}
        extraFooterItems={
          <>
            {supportButton}
            {legalButton}
          </>
        }
      />

      <SupplierMordModal
        isOpen={showLegalModal}
        onClose={closeLegalModal}
        userId={user?.id}
        activeAgreement={activeAgreement}
        loading={legalLoading}
        alreadySigned={!legalLoading && activeAgreement !== null && !hasNewVersion}
        onAccept={acceptAgreement}
        onAcknowledged={() => {
          refreshLegalModal();
          closeLegalModal();
        }}
      />

      <SupportTicketModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        contactEmail={contactEmail}
      />
    </>
  );
}
